// apps/frontend/src/contexts/ToastContext.tsx
// 🎨 Context Toast Centralisé - Compatible avec votre i18n

import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

interface ToastItem {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  translationKey?: string;
  duration?: number;
}

interface ToastContextType {
  toasts: ToastItem[];
  showSuccess: (message: string, translationKey?: string) => void;
  showError: (message: string, translationKey?: string) => void;
  showWarning: (message: string, translationKey?: string) => void;
  showInfo: (message: string, translationKey?: string) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast = useCallback((toast: Omit<ToastItem, 'id'>) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const newToast: ToastItem = {
      id,
      duration: 5000,
      ...toast,
    };

    setToasts(prev => [...prev, newToast]);
    
    // Auto-remove après la durée
    setTimeout(() => {
      removeToast(id);
    }, newToast.duration! + 300);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showSuccess = useCallback((message: string, translationKey?: string) => {
    addToast({ type: 'success', message, translationKey });
  }, [addToast]);

  const showError = useCallback((message: string, translationKey?: string) => {
    addToast({ type: 'error', message, translationKey });
  }, [addToast]);

  const showWarning = useCallback((message: string, translationKey?: string) => {
    addToast({ type: 'warning', message, translationKey });
  }, [addToast]);

  const showInfo = useCallback((message: string, translationKey?: string) => {
    addToast({ type: 'info', message, translationKey });
  }, [addToast]);

  return (
    <ToastContext.Provider value={{
      toasts,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      removeToast,
    }}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
